import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  CreateParticipants,
  CreateWeddingEvent,
  Participant,
  WeddingEvent,
} from '../types/wedding'
import { saveParticipant } from '../api/saveParticipant'
import { getParticipants } from '../api/getParticipants'
import { removeParticipant } from '../api/removeParticipant'
import {
  getWeddingEvents,
  getWeddingEventBySlug,
  createWeddingEvent,
  updateWeddingEvent,
  deleteWeddingEvent,
} from '../api/weddingEvents'

// load participants
export const loadParticipants = createAsyncThunk(
  'wedding/loadParticipants',
  async (eventId: string, thunkApi) => {
    try {
      const actualEventId = eventId === '' ? undefined : eventId
      return await getParticipants(actualEventId)
    } catch (e) {
      return thunkApi.rejectWithValue('load wedding participants Error')
    }
  },
)

// add participant
export const addParticipant = createAsyncThunk(
  'wedding/addParticipant',
  async (participant: CreateParticipants, thunkApi) => {
    try {
      return await saveParticipant(participant)
    } catch (e) {
      return thunkApi.rejectWithValue('add participant Error')
    }
  },
)

// delete participant
export const deleteParticipant = createAsyncThunk(
  'wedding/deleteParticipant',
  async (id: string, thunkApi) => {
    try {
      return await removeParticipant(id)
    } catch (e) {
      return thunkApi.rejectWithValue('delete participant Error')
    }
  },
)

// load wedding events
export const loadWeddingEvents = createAsyncThunk(
  'wedding/loadWeddingEvents',
  async (_, thunkApi) => {
    try {
      return await getWeddingEvents()
    } catch (e) {
      return thunkApi.rejectWithValue('load wedding events Error')
    }
  },
)

// load wedding event by slug
export const loadWeddingEventBySlug = createAsyncThunk(
  'wedding/loadWeddingEventBySlug',
  async (slug: string, thunkApi) => {
    try {
      return await getWeddingEventBySlug(slug)
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.message || e?.message || 'load wedding event Error'
      return thunkApi.rejectWithValue(errorMessage)
    }
  },
)

// create wedding event
export const addWeddingEvent = createAsyncThunk(
  'wedding/addWeddingEvent',
  async (event: CreateWeddingEvent, thunkApi) => {
    try {
      return await createWeddingEvent(event)
    } catch (e) {
      return thunkApi.rejectWithValue('create wedding event Error')
    }
  },
)

// update wedding event
export const updateWeddingEventAction = createAsyncThunk(
  'wedding/updateWeddingEvent',
  async (event: WeddingEvent, thunkApi) => {
    try {
      return await updateWeddingEvent(event)
    } catch (e) {
      return thunkApi.rejectWithValue('update wedding event Error')
    }
  },
)

// delete wedding event
export const removeWeddingEvent = createAsyncThunk(
  'wedding/removeWeddingEvent',
  async (id: string, thunkApi) => {
    try {
      await deleteWeddingEvent(id)
      return id
    } catch (e) {
      return thunkApi.rejectWithValue('delete wedding event Error')
    }
  },
)

interface WeddingSliceState {
  participants: Participant[]
  participantsLoading: boolean
  participantsError?: SerializedError
  weddingEvents: WeddingEvent[]
  weddingEventsLoading: boolean
  weddingEventsError?: SerializedError
  currentWeddingEvent?: WeddingEvent
  currentWeddingEventLoading: boolean
  currentWeddingEventError?: SerializedError
}

const initialState: WeddingSliceState = {
  participants: [],
  participantsLoading: false,
  participantsError: undefined,
  weddingEvents: [],
  weddingEventsLoading: false,
  weddingEventsError: undefined,
  currentWeddingEvent: undefined,
  currentWeddingEventLoading: false,
  currentWeddingEventError: undefined,
}

const weddingSlice = createSlice({
  name: 'wedding',
  initialState,
  reducers: {
    setCurrentWeddingEvent: (
      state,
      action: PayloadAction<WeddingEvent | undefined>,
    ) => {
      state.currentWeddingEvent = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadParticipants.fulfilled, (
      state,
      action: PayloadAction<Participant[]>,
    ) => {
      state.participants = action.payload
      state.participantsLoading = false
      state.participantsError = undefined
    })
    builder.addCase(loadParticipants.pending, (state) => {
      state.participantsLoading = true
      state.participantsError = undefined
    })
    builder.addCase(loadParticipants.rejected, (state, action) => {
      state.participantsLoading = false
      state.participantsError = action.error
    })
    builder.addCase(addParticipant.fulfilled, (
      state,
      action: PayloadAction<Participant[]>,
    ) => {
      state.participants.unshift(...action.payload)
      state.participantsLoading = false
      state.participantsError = undefined
    })
    builder.addCase(addParticipant.pending, (state) => {
      state.participantsLoading = true
      state.participantsError = undefined
    })
    builder.addCase(addParticipant.rejected, (state, action) => {
      state.participantsLoading = false
      state.participantsError = action.error
    })
    builder.addCase(deleteParticipant.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.participants = state.participants.filter(
        (participant) => participant.id !== action.payload,
      )
      state.participantsLoading = false
      state.participantsError = undefined
    })
    builder.addCase(deleteParticipant.pending, (state) => {
      state.participantsLoading = true
      state.participantsError = undefined
    })
    builder.addCase(deleteParticipant.rejected, (state, action) => {
      state.participantsLoading = false
      state.participantsError = action.error
    })
    builder.addCase(loadWeddingEvents.fulfilled, (
      state,
      action: PayloadAction<WeddingEvent[]>,
    ) => {
      state.weddingEvents = action.payload
      state.weddingEventsLoading = false
      state.weddingEventsError = undefined
    })
    builder.addCase(loadWeddingEvents.pending, (state) => {
      state.weddingEventsLoading = true
      state.weddingEventsError = undefined
    })
    builder.addCase(loadWeddingEvents.rejected, (state, action) => {
      state.weddingEventsLoading = false
      state.weddingEventsError = action.error
    })
    builder.addCase(loadWeddingEventBySlug.fulfilled, (
      state,
      action: PayloadAction<WeddingEvent>,
    ) => {
      state.currentWeddingEvent = action.payload
      state.currentWeddingEventLoading = false
      state.currentWeddingEventError = undefined
    })
    builder.addCase(loadWeddingEventBySlug.pending, (state) => {
      state.currentWeddingEventLoading = true
      state.currentWeddingEventError = undefined
    })
    builder.addCase(loadWeddingEventBySlug.rejected, (state, action) => {
      state.currentWeddingEventLoading = false
      state.currentWeddingEventError = action.error
    })
    builder.addCase(addWeddingEvent.fulfilled, (
      state,
      action: PayloadAction<WeddingEvent>,
    ) => {
      state.weddingEvents.unshift(action.payload)
      state.weddingEventsLoading = false
      state.weddingEventsError = undefined
    })
    builder.addCase(addWeddingEvent.pending, (state) => {
      state.weddingEventsLoading = true
      state.weddingEventsError = undefined
    })
    builder.addCase(addWeddingEvent.rejected, (state, action) => {
      state.weddingEventsLoading = false
      state.weddingEventsError = action.error
    })
    builder.addCase(updateWeddingEventAction.fulfilled, (
      state,
      action: PayloadAction<WeddingEvent>,
    ) => {
      state.weddingEvents = state.weddingEvents.map((event) =>
        event.id === action.payload.id ? action.payload : event,
      )
      state.weddingEventsLoading = false
      state.weddingEventsError = undefined
    })
    builder.addCase(updateWeddingEventAction.pending, (state) => {
      state.weddingEventsLoading = true
      state.weddingEventsError = undefined
    })
    builder.addCase(updateWeddingEventAction.rejected, (state, action) => {
      state.weddingEventsLoading = false
      state.weddingEventsError = action.error
    })
    builder.addCase(removeWeddingEvent.fulfilled, (
      state,
      action: PayloadAction<string>,
    ) => {
      state.weddingEvents = state.weddingEvents.filter(
        (event) => event.id !== action.payload,
      )
      state.weddingEventsLoading = false
      state.weddingEventsError = undefined
    })
    builder.addCase(removeWeddingEvent.pending, (state) => {
      state.weddingEventsLoading = true
      state.weddingEventsError = undefined
    })
    builder.addCase(removeWeddingEvent.rejected, (state, action) => {
      state.weddingEventsLoading = false
      state.weddingEventsError = action.error
    })
  },
})

export const { setCurrentWeddingEvent } = weddingSlice.actions
export default weddingSlice.reducer
